/**
 * The platform's motor add-on vocabulary -- the frontend mirror of
 * `backend/apps/quotes/addons.py`. Keep the two lists in step.
 *
 * Each insurer names these differently; the server maps them per insurer and
 * reports back, per option, whether the insurer INCLUDED the add-on (with its
 * premium), does NOT OFFER it for this vehicle, or offers it but NOT ONLINE.
 */

export type AddonCode =
  | 'zero_dep'
  | 'consumables'
  | 'rsa'
  | 'engine_protect'
  | 'return_to_invoice'
  | 'key_replacement'
  | 'tyre_protect'
  | 'ncb_protect'
  | 'personal_belongings'
  | 'pa_unnamed_passenger';

export interface AddonInfo {
  code: AddonCode;
  label: string;
  description: string;
}

export const ADDONS: readonly AddonInfo[] = [
  { code: 'zero_dep', label: 'Zero depreciation', description: 'Claims paid without deducting depreciation on replaced parts.' },
  { code: 'consumables', label: 'Consumables', description: 'Covers nuts, bolts, oils and other consumables used in a repair.' },
  { code: 'rsa', label: 'Roadside assistance', description: 'Towing, flat tyre, battery jump-start and fuel delivery, 24x7.' },
  { code: 'engine_protect', label: 'Engine protection', description: 'Covers engine damage from water ingress or oil leakage.' },
  { code: 'return_to_invoice', label: 'Return to invoice', description: 'On total loss or theft, pays the invoice value, not just the IDV.' },
  { code: 'key_replacement', label: 'Key replacement', description: 'Replaces lost or stolen keys and locks.' },
  { code: 'tyre_protect', label: 'Tyre protection', description: 'Covers accidental damage to tyres and tubes.' },
  { code: 'ncb_protect', label: 'NCB protection', description: 'Keeps your no-claim bonus even after a claim.' },
  { code: 'personal_belongings', label: 'Personal belongings', description: 'Covers loss of personal items kept in the vehicle.' },
  { code: 'pa_unnamed_passenger', label: 'PA cover for passengers', description: 'Personal accident cover for unnamed passengers.' },
];

export const ADDON_CODES: readonly AddonCode[] = ADDONS.map((a) => a.code);

const LABELS = Object.fromEntries(ADDONS.map((a) => [a.code, a.label])) as Record<AddonCode, string>;

export function addonLabel(code: string): string {
  return (LABELS as Record<string, string>)[code] ?? code;
}

/** The IRDAI no-claim-bonus slabs. */
export const NCB_SLABS: readonly number[] = [0, 20, 25, 35, 45, 50];

export type AddonStatus = 'included' | 'not_offered' | 'not_online';

/** One insurer's answer for one requested add-on. */
export interface AddonCoverEntry {
  status: AddonStatus;
  /** What the insurer charged for it, as a 2-place string; null when not known. */
  premium: string | null;
  /** The insurer's own name for it (may name a bundle). */
  label: string;
}

export type AddonCover = Partial<Record<string, AddonCoverEntry>>;

/** Requested add-ons present on an option, in the vocabulary's order. */
export function addonEntries(cover: AddonCover | null | undefined): [AddonCode, AddonCoverEntry][] {
  if (!cover) return [];
  return ADDON_CODES.filter((code) => cover[code]).map((code) => [code, cover[code]!]);
}
